import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllProducts, urlFor } from '../lib/sanity';
import FacebookCTA from '../components/FacebookCTA';

function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'board-game', label: 'Board Games' },
    { value: 'book', label: 'Books' },
    { value: 'extension-kits', label: 'Extension Kits' },
    { value: 'accessories', label: 'Accessories' }
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [searchTerm, selectedCategory, products]);

  const fetchProducts = async () => {
    try {
      const data = await getAllProducts();
      setProducts(data);
      setFilteredProducts(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const filterProducts = () => {
    let filtered = products;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.shortDescription && product.shortDescription.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    setFilteredProducts(filtered);
  };

  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'best-seller':
        return 'from-[#FED700] to-[#F4900C]';
      case 'new':
        return 'from-green-400 to-green-600';
      case 'featured':
        return 'from-blue-400 to-blue-600';
      default:
        return 'from-[#FED700] to-[#F4900C]';
    }
  };

  const getBadgeLabel = (badge) => {
    switch (badge) {
      case 'best-seller':
        return 'Best Seller';
      case 'new':
        return 'New';
      case 'featured':
        return 'Featured';
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#F4900C] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="absolute top-10 left-4 sm:left-10 w-64 h-64 sm:w-96 sm:h-96 bg-[#FED700]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-10 -right-4 sm:right-10 w-64 h-64 sm:w-96 sm:h-96 bg-[#F4900C]/20 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        
        <div className="container-custom relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Our Featured <span className="text-[#F4900C]">Products</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="pb-8">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-4 pr-12 text-lg border-2 border-gray-200 rounded-lg focus:border-[#F4900C] focus:outline-none transition-colors"
                />
                <svg
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 ${
                    selectedCategory === category.value
                      ? 'bg-gradient-to-r from-[#FED700] to-[#F4900C] text-gray-900 shadow-lg'
                      : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#F4900C]'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>

            {/* Results count
            <div className="text-center mt-6 text-gray-600">
              Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            </div> */}
          </div>
        </div>
      </section>

      {/* Products Grid Section */}
      <section className="py-12 pb-20">
        <div className="container-custom">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No products found</h3>
              <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('all');
                }}
                className="text-[#F4900C] hover:underline font-semibold"
              >
                Clear all filters
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {filteredProducts.map((product) => (
                <Link
                  key={product._id}
                  to={`/products/${product.slug}`}
                  className="group bg-white rounded-2xl shadow-lg overflow-hidden border-2 border-transparent hover:border-[#F4900C] transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                >
                  {/* Product Image */}
                  <div className="relative aspect-square bg-gradient-to-br from-yellow-50 to-orange-50 overflow-hidden">
                    <img
                      src={urlFor(product.mainImage).width(600).url()}
                      alt={product.name}
                      className="w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-500"
                    />

                    {/* Badge */}
                    {product.badge && product.badge !== 'none' && (
                      <div className={`absolute top-4 right-4 bg-gradient-to-r ${getBadgeColor(product.badge)} text-white font-bold px-4 py-1 rounded-full text-sm shadow-lg`}>
                        {getBadgeLabel(product.badge)}
                      </div>
                    )}

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                      <span className="text-white font-semibold text-lg flex items-center gap-2">
                        View Details <span className="text-[#FED700]">→</span>
                      </span>
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-6">
                    <div className="mb-3">
                      <h3 className="text-xl font-bold text-gray-900 group-hover:text-[#F4900C] transition-colors mb-2">
                        {product.name}
                      </h3>
                      {product.price && (
                        <span className="text-2xl font-bold text-[#F4900C]">
                          ฿{product.price.toLocaleString()}
                        </span>
                      )}
                    </div>

                    {product.shortDescription && (
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                        {product.shortDescription}
                      </p>
                    )}

                    {/* Product specs */}
                    {(product.age || product.players || product.duration) && (
                      <div className="flex flex-wrap gap-2">
                        {product.age && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {product.age}
                          </span>
                        )}
                        {product.players && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {product.players}
                          </span>
                        )}
                        {product.duration && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full">
                            {product.duration}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contact/Purchase Section */}
      <FacebookCTA
        heading="Interested in Our Products?"
        message="For purchases and inquiries, reach out to us on Facebook."
      />
    </div>
  );
}

export default Products;